import React from "react";
import { motion } from "framer-motion";
import { UserX, Trophy, LogOut, MoonStar } from "lucide-react";
import { Counter } from "./effects/Counter";

const cards = [
  {
    icon: UserX,
    title: "Customers can't find you online.",
    body: "If you don't show up on Google, you don't exist for 4 out of 5 buyers researching before they spend.",
  },
  {
    icon: Trophy,
    title: "Competitors appear more professional.",
    body: "A polished website signals trust, scale and seriousness — even if your service is far better.",
  },
  {
    icon: LogOut,
    title: "Potential leads leave before contacting you.",
    body: "Without a clear CTA, social proof and pricing — visitors bounce in under 8 seconds. Lost forever.",
  },
  {
    icon: MoonStar,
    title: "Your business stops at closing time.",
    body: "A website works while you sleep, takes inquiries on weekends and converts holiday browsers.",
  },
];

const stats = [
  { value: "80%+", label: "Customers research online before buying", anim: { to: 80, suffix: "%+" } },
  { value: "24/7", label: "A website works even when you're sleeping", anim: null },
  { value: "100%", label: "Ownership of your digital presence", anim: { to: 100, suffix: "%" } },
];

export const ProblemSection = () => {
  return (
    <section
      id="problem"
      data-testid="problem-section"
      className="relative py-24 sm:py-32 border-t border-white/5"
    >
      <div className="max-w-7xl mx-auto px-6 sm:px-8">
        <div className="max-w-3xl">
          <div className="text-[11px] tracking-[0.2em] uppercase text-[#D4AF37] mb-4">
            The Cost of Invisibility
          </div>
          <h2 className="font-display text-4xl sm:text-5xl lg:text-6xl tracking-tighter font-light text-white">
            What is your business <span className="gold-text italic">losing</span> without a website?
          </h2>
        </div>

        <div className="mt-16 grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
          {cards.map((c, i) => (
            <motion.div
              key={c.title}
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, amount: 0.3 }}
              transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1], delay: i * 0.07 }}
              data-testid={`problem-card-${i}`}
              className="group relative bg-[#0A0A0A] border border-[#222222] rounded-2xl p-7 transition-all duration-500 hover:border-[#D4AF37]/40 hover:-translate-y-1"
            >
              <div className="w-11 h-11 rounded-xl glass-gold flex items-center justify-center mb-5">
                <c.icon className="w-5 h-5 text-[#D4AF37]" />
              </div>
              <h3 className="font-display text-xl text-white mb-3 leading-snug">
                {c.title}
              </h3>
              <p className="text-sm text-[#B0B0B0] leading-relaxed">{c.body}</p>
              <div className="absolute inset-x-7 bottom-0 h-px bg-gradient-to-r from-transparent via-[#D4AF37]/40 to-transparent opacity-0 group-hover:opacity-100 transition-opacity" />
            </motion.div>
          ))}
        </div>

        <div className="mt-20 glass rounded-3xl p-8 sm:p-12 grid sm:grid-cols-3 gap-8 sm:gap-4 noise">
          {stats.map((s, i) => (
            <motion.div
              key={s.label}
              initial={{ opacity: 0, y: 16 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: i * 0.1 }}
              data-testid={`stat-${i}`}
              className="text-center sm:text-left border-l-0 sm:border-l sm:first:border-l-0 sm:border-[#D4AF37]/15 sm:pl-6 first:pl-0"
            >
              <div className="font-display text-5xl sm:text-6xl text-white tracking-tighter font-light">
                <span className="gold-text">
                  {s.anim ? (
                    <Counter to={s.anim.to} suffix={s.anim.suffix} duration={1600} />
                  ) : (
                    s.value
                  )}
                </span>
              </div>
              <p className="text-sm text-[#B0B0B0] mt-3 max-w-[220px] mx-auto sm:mx-0">
                {s.label}
              </p>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};
